import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, Modal, Select, StatusPill } from "@/components/bits";

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "user" });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteErr, setInviteErr] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/team");
      setData(d);
    } catch (err) {
      if (err.response?.status === 403) setDenied(true);
      else toast.error(err.response?.data?.detail || "Could not load your team");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchMember = (user_id, fields) =>
    setData((d) => (d ? { ...d, members: (d.members || []).map((m) => (m.user_id === user_id ? { ...m, ...fields } : m)) } : d));

  const changeRole = async (m, value) => {
    if (value === m.role) return;
    setRowBusy(m.user_id);
    try {
      await api.post(`/team/${m.user_id}`, { action: "role", value });
      patchMember(m.user_id, { role: value });
      toast.success(`${m.name || m.email} is now ${value === "admin" ? "an admin" : "a user"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change the role");
    } finally {
      setRowBusy(null);
    }
  };

  const toggleStatus = async (m) => {
    const value = m.status === "active" ? "disabled" : "active";
    setRowBusy(m.user_id);
    try {
      await api.post(`/team/${m.user_id}`, { action: "status", value });
      patchMember(m.user_id, { status: value });
      toast.success(value === "active" ? `${m.name || m.email} activated` : `${m.name || m.email} disabled`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update the status");
    } finally {
      setRowBusy(null);
    }
  };

  const sendInvite = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name || !email) {
      setInviteErr("Name and email are required.");
      return;
    }
    setInviteBusy(true);
    setInviteErr(null);
    try {
      await api.post("/team", { name, email, role: form.role });
      toast.success(`Invitation sent to ${email}`);
      setForm({ name: "", email: "", role: "user" });
      setInviteOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not send the invitation";
      setInviteErr(detail);
      toast.error(detail);
    } finally {
      setInviteBusy(false);
    }
  };

  const meRole = data?.me?.role || user?.role || "admin";

  if (denied || (data && meRole !== "admin")) {
    return (
      <div>
        <PageHeader overline="Organisation" title="Team" desc="Members of your organisation, their roles and account status." testId="team-header" />
        <Empty title="Admins only" desc="You need an admin role to manage team members. Ask an organisation admin to upgrade your access." testId="team-not-allowed" />
      </div>
    );
  }

  if (!data) return <p className="text-slate-500 text-sm" data-testid="team-loading">Loading…</p>;

  const members = data.members || [];
  const activeCount = members.filter((m) => m.status === "active").length;
  const myEmail = user?.email;

  return (
    <div>
      <PageHeader
        overline="Organisation"
        title="Team"
        desc="Who can access this workspace, their roles and account status."
        testId="team-header"
        action={<button onClick={() => { setInviteErr(null); setInviteOpen(true); }} className="btn-primary" data-testid="team-invite-btn"><UserPlus className="w-4 h-4" /> Invite member</button>}
      />

      <div className="glass p-6 rise mb-5" data-testid="team-org-card">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="overline-label mb-2">Organisation</p>
            <h2 className="font-display text-xl font-bold text-slate-50" data-testid="team-org-name">{data.org?.name || "Your organisation"}</h2>
            <p className="text-sm text-slate-400 mt-1" data-testid="team-org-domain">
              {data.org?.domain ? <>Domain · <span className="font-mono text-slate-300">{data.org.domain}</span></> : "No email domain set"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="pill" data-testid="team-member-count">{members.length} member{members.length === 1 ? "" : "s"}</span>
            <span className="pill-muted" data-testid="team-active-count">{activeCount} active</span>
          </div>
        </div>
      </div>

      <div className="glass overflow-hidden rise" data-testid="team-members">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr>
                <th className="table-head">Member</th>
                <th className="table-head">Email</th>
                <th className="table-head">Role</th>
                <th className="table-head">Status</th>
                <th className="table-head">Joined</th>
                <th className="table-head text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && (
                <tr><td className="table-cell text-slate-500" colSpan={6}>No members yet — invite your first teammate.</td></tr>
              )}
              {members.map((m) => {
                const self = myEmail && m.email === myEmail;
                const busy = rowBusy === m.user_id;
                return (
                  <tr key={m.user_id || m.email} className="hover:bg-white/[0.02]" data-testid={`team-row-${m.email}`}>
                    <td className="table-cell">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 shrink-0 rounded-full bg-emerald-900/80 border border-white/10 flex items-center justify-center text-xs font-mono text-emerald-200">{(m.name || m.email || "?")[0].toUpperCase()}</div>
                        <p className="text-slate-100 font-medium">
                          {m.name || "—"}
                          {self && <span className="ml-2 px-1.5 py-0.5 rounded-full font-mono text-[10px] uppercase bg-white/5 border border-white/10 text-slate-400" data-testid="team-you-pill">you</span>}
                        </p>
                      </div>
                    </td>
                    <td className="table-cell">
                      <p className="text-xs text-slate-400">{m.email}</p>
                      {!m.email_verified && <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full font-mono text-[10px] uppercase bg-amber-950/70 border border-amber-500/30 text-amber-300">unverified</span>}
                    </td>
                    <td className="table-cell">
                      <Select value={m.role} onChange={(v) => changeRole(m, v)} options={ROLE_OPTIONS} className="w-28" testId={`team-role-${m.email}`} />
                    </td>
                    <td className="table-cell"><StatusPill status={m.status} /></td>
                    <td className="table-cell text-xs text-slate-400 whitespace-nowrap">{fmtDate(m.created_at)}</td>
                    <td className="table-cell text-right">
                      <button onClick={() => toggleStatus(m)} disabled={busy} className={`btn-sm ${m.status === "active" ? "btn-ghost" : "btn-primary"}`} data-testid={`team-toggle-${m.email}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : m.status === "active" ? "Disable" : "Activate"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite a team member" testId="team-invite-modal">
        <div className="space-y-4">
          <div>
            <label className="field-label" htmlFor="team-invite-name">Name</label>
            <input id="team-invite-name" className="field" placeholder="e.g. Anita Deshmukh" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-invite-name" />
          </div>
          <div>
            <label className="field-label" htmlFor="team-invite-email">Business email</label>
            <input id="team-invite-email" type="email" className="field" placeholder="name@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-invite-email" />
          </div>
          <div>
            <label className="field-label">Role</label>
            <Select value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={[{ value: "user", label: "User — run campaigns" }, { value: "admin", label: "Admin — full access & team" }]} testId="team-invite-role" />
          </div>

          {inviteErr && <p className="text-xs text-rose-300 bg-rose-950/40 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="team-invite-error">{inviteErr}</p>}

          <button onClick={sendInvite} disabled={inviteBusy} className="btn-primary w-full" data-testid="team-invite-submit">
            {inviteBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Send invitation"}
          </button>
          <p className="text-[11px] text-slate-500 text-center">They'll join {data.org?.name || "the organisation"} once they verify their email.</p>
        </div>
      </Modal>
    </div>
  );
}
